import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareBar from '@/components/feature/ShareBar';
import EmptyState from '@/components/base/EmptyState';
import CultureCard from '@/components/feature/CultureCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getCultureById, getRelatedCulture, formatDateCN } from '@/lib/content';

export default function CultureDetailPage() {
  const { id } = useParams();
  const item = getCultureById(id);

  usePageMeta({
    title: item ? `${item.title} - 琉璃神社` : '文化专题不存在 - 琉璃神社',
    description: item
      ? `${item.summary.slice(0, 80)} 琉璃神社文化专题，动漫文化深度解读与分享。`
      : '该文化专题不存在或已被删除，返回琉璃神社文化分类查看更多内容。',
    keywords: item ? `${item.tags.join(',')},动漫文化,文化专题,琉璃神社` : '动漫文化,琉璃神社',
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="shell py-16 md:py-24">
          <Breadcrumb items={[{ label: '动漫文化', to: '/category/culture' }, { label: '专题不存在' }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-error-warning-line"
              title="文化专题不存在或已删除"
              description="这个专题可能已经调整，或者链接地址有误。回到文化分类，看看其他值得慢读的内容。"
              actionLabel="返回文化专题"
              actionTo="/category/culture"
            />
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedCulture(item.id, 3);

  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: '动漫文化', to: '/category/culture' }, { label: '文化专题' }]} />
          <div className="mt-5 max-w-4xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 rounded bg-primary-500 text-background-50 text-[11px] leading-none">
                文化专题
              </span>
              {item.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded bg-background-50 border border-background-200 text-[11px] text-secondary-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
            <h1 className="mt-4 text-2xl md:text-[38px] md:leading-[1.35] font-semibold tracking-wide">
              {item.title}
            </h1>
            <p className="mt-2 font-heading text-base md:text-lg text-accent-700">{item.subtitle}</p>
            <p className="mt-4 text-sm md:text-base text-secondary-600 leading-7 max-w-3xl">{item.summary}</p>
            <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] md:text-xs text-secondary-500 font-label">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {item.author}
                </span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{formatDateCN(item.date)}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{item.readTime}阅读</span>
              </div>
              <ShareBar title={item.title} />
            </div>
          </div>
        </div>
      </div>

      <div className="shell py-8 md:py-12">
        <div className="w-full max-w-5xl mx-auto">
          <div className="w-full aspect-[16/9] md:aspect-[16/8] rounded-lg overflow-hidden bg-background-200 border border-background-200/80">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 琉璃神社文化专题`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-9 max-w-3xl mx-auto">
            <p className="text-[15px] md:text-lg text-foreground-800 leading-8 md:leading-9 font-medium">
              {item.summary}
            </p>

            {item.content.map((section, index) => (
              <section key={section.heading} className="mt-9">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-label text-accent-600 tracking-[0.2em]">
                    0{index + 1}
                  </span>
                  <span className="w-6 h-px bg-accent-500"></span>
                  <h2 className="text-lg md:text-xl font-semibold">{section.heading}</h2>
                </div>
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 14)}
                    className="text-[15px] md:text-base text-foreground-700 leading-[1.95] mb-5"
                  >
                    {paragraph}
                  </p>
                ))}
                {index === 0 && (
                  <blockquote className="my-8 border-l-2 border-accent-500 bg-background-100/80 rounded-r-md px-5 py-5">
                    <p className="font-heading text-base md:text-xl text-foreground-900 leading-8 md:leading-9">
                      {item.quote}
                    </p>
                    <p className="mt-2.5 text-[11px] text-secondary-500 tracking-wide">
                      — {item.author}，琉璃神社文化专题
                    </p>
                  </blockquote>
                )}
              </section>
            ))}

            <div className="mt-10 flex flex-wrap items-center gap-2 pt-6 border-t border-background-200/70">
              <span className="text-[11px] tracking-[0.16em] uppercase text-secondary-500 font-label mr-1">
                Tags
              </span>
              {item.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-2.5 py-1 rounded bg-secondary-100 text-[11px] text-secondary-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <Link
                to="/category/culture"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回文化专题
              </Link>
              <ShareBar title={item.title} />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-background-100/70 border-y border-background-200/70 py-10 md:py-14">
        <div className="shell">
          <div className="flex items-end justify-between gap-3 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-px bg-accent-500"></span>
                <span className="text-[11px] tracking-[0.24em] uppercase text-accent-600 font-label">
                  More Essays
                </span>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold">相关文化专题</h2>
            </div>
            <Link
              to="/category/culture"
              className="text-xs text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer hidden sm:inline-flex items-center gap-1"
            >
              全部专题
              <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((rel) => (
              <CultureCard key={rel.id} item={rel} />
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}